import { useEffect, useState } from 'react';
import {
  CampaignDetailModel,
  CampaignDetailModelState,
  ContractCompletionType,
  UploadedDocumentDocumentType,
  FormType,
  useCompleteCampaignStep,
  useGetUploadedFileLink,
} from '@urbanx/agx.campaigns.client';
import { NswContractStage, StakeholderType } from '@urbanx/agx-ui-components';
import {
  AgxButton,
  AgxSpacer,
  AgxTypography,
  useToast,
  useWindowSize,
  Breakpoints,
} from '@urbanx/agx.ui-components2';
import { radius, tokens } from '@urbanx/agx.ui-components2/tokens';
import placeHolderImage from 'assets/images/placeholder-property.png';
import { useAppSelector } from 'hooks/useAppSelector';
import { useFormLoader } from 'hooks/useFormLoader';
import { useNewSelectedContract } from 'hooks/useNewSelectedContract';
import { cleanTwoLineAddress } from 'utils/formatAddress';
import { formatListOfStrings } from 'utils/formatString';
import { getAllBuyerNames, prepareSignatories } from 'utils/vendorBuyerUtil';
import { formatCurrencyWithConditionalDecimals } from 'utils/formatNumber';
import { Tabs } from 'types/Tabs';
import { openFileInNewTab } from 'utils/openFileInNewTab';
import { getEnumValue } from 'helpers/enumHelper';
import {
  ContractFileList,
  contractSubmissionErrorMessage,
  createFileDownloadOptions,
  getContractStageLabel,
  listDetailSeparator,
  withCampaignFormConfig,
} from './ContractSubmitPanel.helpers';

interface ContractSubmitPanelProps {
  campaign: CampaignDetailModel;
  currentTab: Tabs;
  showDownload?: boolean;
  showSubmit?: boolean;
}

const ContractSubmitPanel = ({
  campaign,
  showDownload = true,
  showSubmit = false,
}: ContractSubmitPanelProps) => {
  const contract = useNewSelectedContract();
  const { loadFormWithConfig } = useFormLoader();
  const { pushToast } = useToast();
  const availableForms = useAppSelector((state) => state.forms.availableForms);
  const [downloadFilePath, setDownloadFilePath] = useState<string>();
  const { data: downloadFileLink, isError: downloadFileLinkError } = useGetUploadedFileLink(
    downloadFilePath ?? '',
    !!downloadFilePath
  );
  const { completeCampaignStep, completeCampaignStepPending } = useCompleteCampaignStep({
    onSuccess: () => {},
    onError: () => {},
  });
  const { buyers, documents, salePrice } = contract || {};
  const { address, state, propertyImageUrls: { medium: imageUrl = null } = {} } = campaign;

  if (!contract?.contractStage) throw new Error('Contract stage is undefined');

  const contractStage = getContractStageLabel(state, contract);
  const isPendingSolicitorExchange =
    state === CampaignDetailModelState.NSW &&
    (getEnumValue(NswContractStage, contract.contractStage) ?? contract.contractStage) ===
      NswContractStage.PendingSolicitorExchange;
  const isConvertedSolicitorExchange =
    isPendingSolicitorExchange &&
    contract.contractCompletionType === ContractCompletionType.SalesAdvice &&
    contract.hasChangedExchangeMethod;
  const salesAdvice = documents?.find(
    (document) => document.documentType === UploadedDocumentDocumentType.SalesAdvice
  );

  const [addressLineOne, addressLineTwo] = cleanTwoLineAddress(address);

  const allBuyers = prepareSignatories(buyers, StakeholderType.Buyer);

  const allBuyerNames = getAllBuyerNames(allBuyers);

  const screenSize = useWindowSize();

  const isDesktop = screenSize === Breakpoints.Desktop;

  useEffect(() => {
    if (!downloadFilePath || !downloadFileLink) return;

    openFileInNewTab(isDesktop, downloadFileLink);
    setDownloadFilePath(undefined);
  }, [downloadFilePath, downloadFileLink]);

  useEffect(() => {
    if (!downloadFileLinkError) return;

    setDownloadFilePath(undefined);
  }, [downloadFileLinkError]);

  const beginSubmission = async () => {
    const contractSubmissionForm = availableForms.find(
      (form) =>
        form.type === FormType.ContractSubmission &&
        form.state?.toString() === campaign.state?.toString()
    );

    if (!contractSubmissionForm || !campaign.campaignId || !contract?.contractId) return;

    try {
      const newForm = await completeCampaignStep({
        campaignId: campaign.campaignId,
        forcedCompletion: true,
        formId: contract.contractId,
      });

      if (!newForm) {
        pushToast({
          type: 'error',
          message: contractSubmissionErrorMessage,
        });
        return;
      }

      await loadFormWithConfig(withCampaignFormConfig(newForm));
    } catch (err) {
      console.error(err);
      pushToast({
        type: 'error',
        message: contractSubmissionErrorMessage,
      });
    }
  };

  const fileDownloadOptions = createFileDownloadOptions(documents, setDownloadFilePath);

  return (
    <AgxSpacer fill contentAlignment="center" gap="spacing-xl">
      <AgxTypography
        id="contract-submit-panel-address"
        type="heading"
        size="2x-large"
        style={{ textAlign: 'center' }}
      >
        {addressLineOne},
        <br />
        {addressLineTwo}
      </AgxTypography>
      <AgxTypography
        id="contract-submit-panel-buyers-sale-price"
        type="body"
        size="medium"
        style={{ textAlign: 'center' }}
      >
        {formatListOfStrings(allBuyerNames)} {listDetailSeparator}{' '}
        {formatCurrencyWithConditionalDecimals(salePrice ?? 0, 2)}
      </AgxTypography>
      <AgxSpacer
        direction="horizontal"
        contentAlignment="center"
        contentJustification="center"
        className="listing-imageCard"
      >
        <div
          style={{
            backgroundImage: `url("${imageUrl || placeHolderImage}")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            aspectRatio: '2/1',
          }}
          className={`imageCard-image`}
        >
          <div className="image-tag">
            <AgxTypography
              id="contract-submit-panel-stage"
              type="label"
              size="medium"
              color="var(--brand-text-button-color)"
            >
              {contractStage}
            </AgxTypography>
          </div>
        </div>
      </AgxSpacer>
      {isPendingSolicitorExchange ? (
        <>
          {showDownload && (
            <AgxButton
              id="download-sales-advice"
              variant="hollow"
              size="large"
              text="Download Sales Advice"
              disabled={!salesAdvice?.containerFilePath}
              onClick={() => setDownloadFilePath(salesAdvice?.containerFilePath)}
            />
          )}
          {isConvertedSolicitorExchange && (
            <AgxSpacer
              fill
              contentAlignment="center"
              padding={{
                top: 'spacing-6xl',
                bottom: 'spacing-6xl',
                left: 'spacing-5xl',
                right: 'spacing-5xl',
              }}
              style={{ background: tokens.bg.tagLightGrey, borderRadius: radius['2xl'] }}
            >
              <AgxTypography
                id="solicitor-exchange-conversion-notice"
                type="body"
                size="small"
                color={tokens.text.tertiary}
                textAlign="center"
              >
                This Contract was converted from an Agent Exchange to a Solicitor Exchange
              </AgxTypography>
            </AgxSpacer>
          )}
        </>
      ) : (
        <ContractFileList
          documents={documents}
          showDownload={showDownload}
          state={state}
          fileDownloadOptions={fileDownloadOptions}
        />
      )}
      {showSubmit && (
        <AgxButton
          id="startsubmission"
          variant="primary"
          size="large"
          text="Submit Contract"
          onClick={beginSubmission}
          disabled={completeCampaignStepPending}
        />
      )}
    </AgxSpacer>
  );
};

export default ContractSubmitPanel;
